import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Mail, UserPlus, Twitter, Instagram, Github, BookOpen, MessageSquare, HelpCircle } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function CTA({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none reverse' } }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  const contactCards = [
    { icon: UserPlus, title: 'Request access', description: 'Join the early access list.', cta: 'Get started', href: '/signin' },
    { icon: Calendar, title: 'Book a demo', description: 'See the workflow in 15 minutes.', cta: 'Schedule', href: 'mailto:hello@event.tools' },
    { icon: Mail, title: 'Email us', description: 'hello@event.tools', cta: 'Send email', href: 'mailto:hello@event.tools' },
  ];

  return (
    <section ref={sectionRef} id="cta" className={`relative bg-[#0B0F10] pt-14 pb-10 ${className}`}>
      <div className="absolute top-0 left-0 right-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(11, 15, 16, 0.8), transparent)' }} />
      <div ref={contentRef} className="relative z-10 px-6 lg:px-12" style={{ willChange: 'transform, opacity' }}>
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-display font-bold text-[clamp(36px,5vw,72px)] leading-[1] tracking-[-0.03em] text-white mb-6">
            Ready to streamline<br />
            <span className="text-[#2EC4B6]">your flyers?</span>
          </h2>
          <p className="text-[clamp(16px,1.2vw,20px)] leading-relaxed text-[#A9B4B8]">
            Get early access to Event.Tools and never hunt for assets again.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-12">
          {contactCards.map((card, index) => (
            card.href.startsWith('/') ? (
              <Link
                key={index}
                to={card.href}
                className="group relative bg-[#122A2F]/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-[#2EC4B6]/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-lg bg-[#2EC4B6]/20 flex items-center justify-center mb-4 group-hover:bg-[#2EC4B6]/30 transition-colors">
                  <card.icon size={24} className="text-[#2EC4B6]" />
                </div>
                <h3 className="font-display font-semibold text-xl text-white mb-2">{card.title}</h3>
                <p className="text-[#A9B4B8] text-sm mb-4">{card.description}</p>
                <div className="flex items-center gap-2 text-[#2EC4B6] text-sm font-medium group-hover:gap-3 transition-all">
                  {card.cta}
                  <ArrowRight size={16} />
                </div>
              </Link>
            ) : (
              <a
                key={index}
                href={card.href}
                className="group relative bg-[#122A2F]/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-[#2EC4B6]/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-lg bg-[#2EC4B6]/20 flex items-center justify-center mb-4 group-hover:bg-[#2EC4B6]/30 transition-colors">
                  <card.icon size={24} className="text-[#2EC4B6]" />
                </div>
                <h3 className="font-display font-semibold text-xl text-white mb-2">{card.title}</h3>
                <p className="text-[#A9B4B8] text-sm mb-4">{card.description}</p>
                <div className="flex items-center gap-2 text-[#2EC4B6] text-sm font-medium group-hover:gap-3 transition-all">
                  {card.cta}
                  <ArrowRight size={16} />
                </div>
              </a>
            )
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-10 border-y border-white/10 my-10 max-w-4xl mx-auto">
          <a href="#workflow" className="flex items-center gap-3 text-[#A9B4B8] hover:text-[#2EC4B6] transition-colors group">
            <BookOpen size={18} className="flex-shrink-0 text-[#2EC4B6]/70 group-hover:text-[#2EC4B6]" />
            <span className="text-sm">How it works</span>
          </a>
          <a href="#features" className="flex items-center gap-3 text-[#A9B4B8] hover:text-[#2EC4B6] transition-colors group">
            <HelpCircle size={18} className="flex-shrink-0 text-[#2EC4B6]/70 group-hover:text-[#2EC4B6]" />
            <span className="text-sm">Features</span>
          </a>
          <a href="mailto:hello@event.tools" className="flex items-center gap-3 text-[#A9B4B8] hover:text-[#2EC4B6] transition-colors group">
            <MessageSquare size={18} className="flex-shrink-0 text-[#2EC4B6]/70 group-hover:text-[#2EC4B6]" />
            <span className="text-sm">Contact</span>
          </a>
          <Link to="/signin" className="flex items-center gap-3 text-[#A9B4B8] hover:text-[#2EC4B6] transition-colors group">
            <UserPlus size={18} className="flex-shrink-0 text-[#2EC4B6]/70 group-hover:text-[#2EC4B6]" />
            <span className="text-sm">Get started</span>
          </Link>
        </div>
        <footer className="border-t border-white/10 pt-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <img src="/eventtools-logo.svg" alt="Event.Tools" className="w-10 h-10 object-contain" />
              <div>
                <span className="font-display font-bold text-lg text-white">Event.Tools</span>
                <p className="text-xs text-[#A9B4B8]">AI flyers with your assets.</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <a href="#features" className="text-sm text-[#A9B4B8] hover:text-white transition-colors">Features</a>
              <a href="#workflow" className="text-sm text-[#A9B4B8] hover:text-white transition-colors">How it Works</a>
              <Link to="/signin" className="text-sm text-[#A9B4B8] hover:text-white transition-colors">Get started</Link>
              <Link to="/terms" className="text-sm text-[#A9B4B8] hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="text-sm text-[#A9B4B8] hover:text-white transition-colors">Privacy Policy</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://twitter.com/eventtools" className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#2EC4B6]/20 transition-colors">
                <Twitter size={18} className="text-[#A9B4B8]" />
              </a>
              <a href="https://instagram.com/eventtools" className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#2EC4B6]/20 transition-colors">
                <Instagram size={18} className="text-[#A9B4B8]" />
              </a>
              <a href="https://github.com/eventtools" className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#2EC4B6]/20 transition-colors">
                <Github size={18} className="text-[#A9B4B8]" />
              </a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/5 text-center flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <p className="text-xs text-[#A9B4B8]/60">© {new Date().getFullYear()} Event.Tools. All rights reserved.</p>
            <span className="hidden sm:inline text-[#A9B4B8]/40">·</span>
            <div className="flex gap-4">
              <Link to="/terms" className="text-xs text-[#A9B4B8]/60 hover:text-[#2EC4B6] transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="text-xs text-[#A9B4B8]/60 hover:text-[#2EC4B6] transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
}
